'use client';

import * as React from 'react';
import { type BrandTheme, CerLogo } from '../../atoms/brand-logo/brand-logo';

export interface AuthScreenProps {
	/** Contenuto della pagina di autenticazione (tipicamente {@link AuthHeader} + form). */
	children: React.ReactNode;
}

/** Contenitore a tutto schermo che centra il contenuto delle pagine di login/registrazione/recupero password. */
const AuthScreen = ({ children }: AuthScreenProps) => (
	<div className="flex min-h-screen items-center justify-center bg-bg px-4">
		<div className="w-full max-w-md">{children}</div>
	</div>
);
AuthScreen.displayName = 'AuthScreen';

export interface AuthHeaderProps {
	/**
	 * Tema corrente dell'app: sceglie la variante del wordmark. Se assente mostra
	 * il marchio testuale SLIMS CER.
	 *
	 * È un tema e non un URL perché "quale file per quale tema" è una decisione di
	 * presentazione, e sta in {@link CerLogo} insieme all'altra: la pagina di login
	 * passava l'asset scuro fisso e sul tema chiaro mostrava il wordmark sbagliato.
	 */
	theme?: BrandTheme;
	/** Titolo della pagina (es. "Accedi"). */
	title: React.ReactNode;
	/** Sottotitolo sotto il titolo. */
	subtitle?: React.ReactNode;
}

/** Intestazione centrata delle pagine di autenticazione: logo, titolo e sottotitolo. */
const AuthHeader = ({ theme, title, subtitle }: AuthHeaderProps) => (
	<div className="text-center">
		{theme ? (
			<div className="mb-4 flex justify-center">
				<CerLogo theme={theme} size="auth" />
			</div>
		) : (
			<div className="mb-4 inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-accent to-accent-purple px-4 py-2 font-mono text-sm font-bold text-black">
				SLIMS
				<span className="text-xs font-bold tracking-[3px] text-white/90">CER</span>
			</div>
		)}
		<h1 className="text-xl font-bold text-text-primary">{title}</h1>
		{subtitle && <p className="mt-1 text-xs text-text-muted">{subtitle}</p>}
	</div>
);
AuthHeader.displayName = 'AuthHeader';

export interface AuthNoticeProps {
	/** Messaggio di errore da mostrare. */
	children: React.ReactNode;
}

/** Avviso di errore (rosso) sopra il form di autenticazione. */
const AuthNotice = ({ children }: AuthNoticeProps) => (
	<div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
		{children}
	</div>
);
AuthNotice.displayName = 'AuthNotice';

export interface AuthFieldProps {
	/** Id del campo, collega l'etichetta all'input. */
	id: string;
	/** Etichetta del campo. */
	label: React.ReactNode;
	/** Tipo di input HTML. Default: `text`. */
	type?: 'text' | 'email' | 'password';
	/** Valore corrente del campo. */
	value: string;
	/** Chiamata a ogni digitazione con il nuovo valore. */
	onChange: (v: string) => void;
	/** Testo segnaposto. */
	placeholder?: string;
	/** Se true, il campo è obbligatorio per il submit del form. */
	required?: boolean;
	/** Lunghezza minima del valore (validazione nativa). */
	minLength?: number;
}

/** Campo di form etichettato per le pagine di autenticazione (email, password, ecc.). */
const AuthField = ({
	id,
	label,
	type = 'text',
	value,
	onChange,
	placeholder,
	required,
	minLength,
}: AuthFieldProps) => (
	<div>
		<label htmlFor={id} className="mb-1 block text-xs font-semibold text-text-muted">
			{label}
		</label>
		<input
			id={id}
			type={type}
			value={value}
			onChange={(e) => onChange(e.target.value)}
			required={required}
			minLength={minLength}
			className="w-full rounded-[7px] border border-border bg-card px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-accent"
			placeholder={placeholder}
		/>
	</div>
);
AuthField.displayName = 'AuthField';

export interface AuthSubmitProps {
	/** Se true, disabilita il bottone (es. durante l'invio). */
	disabled?: boolean;
	/** Testo del bottone. */
	children: React.ReactNode;
}

/** Bottone di submit a piena larghezza per i form di autenticazione. */
const AuthSubmit = ({ disabled, children }: AuthSubmitProps) => (
	<button
		type="submit"
		disabled={disabled}
		className="w-full rounded-[7px] bg-accent px-4 py-2.5 text-sm font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
	>
		{children}
	</button>
);
AuthSubmit.displayName = 'AuthSubmit';

export interface AuthNoteProps {
	/** Colore del testo. Default: `muted`. */
	tone?: 'muted' | 'success';
	/** Testo della nota. */
	children: React.ReactNode;
}

/** Testo di aiuto piccolo e centrato sotto il form (es. link "password dimenticata"). */
const AuthNote = ({ tone = 'muted', children }: AuthNoteProps) => (
	<p
		className={
			tone === 'success'
				? 'text-center text-xs text-accent-green'
				: 'text-center text-xs text-text-muted'
		}
	>
		{children}
	</p>
);
AuthNote.displayName = 'AuthNote';

export interface AuthTextButtonProps {
	/** Chiamata al click. */
	onClick: () => void;
	/** Se true, disabilita il bottone. */
	disabled?: boolean;
	/** Testo del bottone. */
	children: React.ReactNode;
}

/**
 * Bottone testuale centrato per le azioni secondarie delle pagine di
 * autenticazione (es. "Password dimenticata?", "Torna all'accesso").
 */
const AuthTextButton = ({ onClick, disabled, children }: AuthTextButtonProps) => (
	<div className="text-center">
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className="text-xs font-semibold text-accent underline-offset-2 transition-opacity hover:underline disabled:opacity-50"
		>
			{children}
		</button>
	</div>
);
AuthTextButton.displayName = 'AuthTextButton';

export interface AuthInlineEmailFormProps {
	/** Valore corrente del campo email. */
	email: string;
	/** Chiamata a ogni digitazione nel campo email. */
	onEmailChange: (v: string) => void;
	/** Chiamata al submit del form. */
	onSubmit: (e: React.FormEvent) => void;
	/** Se true, disabilita il bottone e mostra "..." al posto del testo. */
	loading: boolean;
	/** Nota mostrata sopra il campo. */
	hint?: React.ReactNode;
	/** Testo del bottone di invio. Default: "Invia". */
	submitLabel?: React.ReactNode;
}

/** Riga compatta email + submit (es. recupero password dalla pagina di login). */
const AuthInlineEmailForm = ({
	email,
	onEmailChange,
	onSubmit,
	loading,
	hint,
	submitLabel = 'Invia',
}: AuthInlineEmailFormProps) => (
	<form onSubmit={onSubmit} className="space-y-2">
		{hint && <AuthNote>{hint}</AuthNote>}
		<div className="flex gap-2">
			<input
				type="email"
				value={email}
				onChange={(e) => onEmailChange(e.target.value)}
				required
				placeholder="La tua email"
				className="flex-1 rounded-[7px] border border-border bg-card px-3 py-2 text-xs text-text-primary outline-none focus:border-accent"
			/>
			<button
				type="submit"
				disabled={loading}
				className="rounded-[7px] bg-accent px-3 py-2 text-xs font-bold text-black disabled:opacity-50"
			>
				{loading ? '...' : submitLabel}
			</button>
		</div>
	</form>
);
AuthInlineEmailForm.displayName = 'AuthInlineEmailForm';

export {
	AuthField,
	AuthHeader,
	AuthInlineEmailForm,
	AuthNote,
	AuthNotice,
	AuthScreen,
	AuthSubmit,
	AuthTextButton,
};
